import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  TextField,
  Stack,
  Typography,
  Autocomplete,
  createFilterOptions,
} from '@mui/material';
import type { BillingTerm, ProductsListItem } from '@techgitter/contracts';
import { SidePanel } from '../panel/SidePanel';
import { FormField } from '../panel/FormField';
import { SmartSelect } from '../dropdown';
import { createVendorProduct } from '../../services/vendorProductService';
import { createProductMapping } from '../../services/productMappingService';
import { fetchInventory } from '../../services/inventoryApi';
import { LICENSE_CATEGORIES, getLicenseCategoryForSku } from '../../data/licenseCategories';
import './reconciliationPanel.css';

// Explicit substring filter on name + sku (not the default, which can drift to
// the verbose description and is hard to reason about). Trimmed + capped so the
// ~hundreds-of-licenses list stays responsive.
const filterProducts = createFilterOptions<ProductsListItem>({
  trim: true,
  limit: 50,
  stringify: (o) => `${o.displayName ?? ''} ${o.sku ?? ''}`,
});

const BILLING_TERM_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Annual' },
];

// Distinct SKU prefixes that identify a license product (e.g. BML, BGL).
const LICENSE_SKU_PREFIXES = Array.from(
  new Set(LICENSE_CATEGORIES.flatMap((c) => c.skuPrefixes)),
);

interface Props {
  open: boolean;
  vendorId: string;
  vendorName?: string;
  /** Pre-filled SKU from the unmatched import row. */
  initialSku: string;
  /** Pre-filled billing term from the unmatched import row (when known). */
  initialBillingTerm?: BillingTerm;
  onClose: () => void;
  /**
   * Called after a successful create. `reconcile` reflects the "reconcile
   * affected records" checkbox; `sku` is the created vendor product's SKU (the
   * scope for that targeted reconcile). The parent refreshes the worklist either
   * way.
   */
  onCreated: (opts: { reconcile: boolean; sku: string }) => void;
}

/**
 * Creates a vendor product (supplier SKU) for an unmatched import SKU — one of
 * the two reference-data create actions on the reconciliation worklist. The SKU
 * is the matching key the engine uses, so it's pre-filled and the operator just
 * confirms the display name + billing term.
 *
 * Optionally also links the new vendor product to a commercial product
 * (cr001_product_mappings) so it feeds the billing/coverage control — the
 * vendor product alone only fixes the cost-side import match.
 *
 * Presented as an on-system panel (the shared SidePanel shell) rendered inside a
 * chrome-stripped MUI Dialog so it keeps the centred overlay + backdrop the
 * worklist uses while looking like the rest of the app's panels.
 */
export const CreateVendorProductDialog: React.FC<Props> = ({
  open,
  vendorId,
  vendorName,
  initialSku,
  initialBillingTerm,
  onClose,
  onCreated,
}) => {
  const [name, setName] = useState(initialSku);
  const [sku, setSku] = useState(initialSku);
  const [billingTerm, setBillingTerm] = useState<BillingTerm>(
    initialBillingTerm ?? 'annual',
  );

  const [linkProduct, setLinkProduct] = useState(false);
  const [products, setProducts] = useState<ProductsListItem[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductsListItem | null>(null);
  const [seatMultiplier, setSeatMultiplier] = useState(1);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [reconcileAfter, setReconcileAfter] = useState(true);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load commercial products when the operator opts to link one. The catalog
  // can exceed the API's page cap (200), so a single page would silently drop
  // most licenses (they're scattered by display name). Instead, license-only
  // mode fetches each license SKU prefix server-side (search=BML, search=BGL)
  // and merges — total catalog size no longer matters. "Show all" falls back to
  // a single page (escape hatch; the typeahead narrows it).
  // Keyed on linkProduct + showAllCategories; state set here is NOT a dep, which
  // would otherwise re-trigger the effect and abort its own fetch.
  useEffect(() => {
    if (!linkProduct) return;
    let active = true;
    const controller = new AbortController();
    const signal = controller.signal;
    setProductsLoading(true);

    const load = async (): Promise<ProductsListItem[]> => {
      if (showAllCategories) {
        const res = await fetchInventory(
          { page: 1, pageSize: 200, sort: 'displayName', order: 'asc', signal },
        );
        return res.items;
      }
      const batches = await Promise.all(
        LICENSE_SKU_PREFIXES.map((prefix) =>
          fetchInventory(
            { page: 1, pageSize: 200, search: prefix, sort: 'displayName', order: 'asc', signal },
          ).then((r) => r.items),
        ),
      );
      const byId = new Map<string, ProductsListItem>();
      batches.flat().forEach((it) => byId.set(it.id, it));
      return Array.from(byId.values());
    };

    load()
      .then((items) => {
        if (active) setProducts(items);
      })
      .catch((e) => {
        if (!active || (e instanceof Error && e.name === 'AbortError')) return;
        setError('Could not load the commercial product list.');
      })
      .finally(() => {
        if (active) setProductsLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [linkProduct, showAllCategories]);

  const productOptions = useMemo(
    () =>
      products
        // Never offer inactive/disabled products.
        .filter((p) => !p.disabled)
        // Default to licenses only. Identify them by SKU prefix via the shared
        // licenseCategories registry (BML / BGL) rather than the
        // cr001_product_category field, which is largely untagged in the catalog.
        // (The fetch already searched by prefix; this guards against substring
        // search false-positives.)
        .filter((p) => showAllCategories || getLicenseCategoryForSku(p.sku) !== null)
        .sort((a, b) => (a.displayName ?? '').localeCompare(b.displayName ?? '')),
    [products, showAllCategories],
  );

  const submit = async () => {
    if (!sku.trim() || !name.trim()) {
      setError('Name and SKU are required.');
      return;
    }
    if (linkProduct && !selectedProduct) {
      setError('Pick a commercial product to link, or uncheck the link option.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await createVendorProduct({
        vendorId,
        name: name.trim(),
        sku: sku.trim(),
        billingTerm,
        isActive: true,
      });

      if (linkProduct && selectedProduct) {
        // The vendor product already exists now; if the mapping fails, surface a
        // warning rather than implying a rollback (it didn't roll back).
        try {
          await createProductMapping({
            productId: selectedProduct.id,
            vendorProductId: created.id,
            seatMultiplier: Math.max(1, Number(seatMultiplier) || 1),
          });
        } catch (mapErr) {
          setError(
            `Vendor product created, but linking to the commercial product failed: ${
              mapErr instanceof Error ? mapErr.message : 'unknown error'
            }. You can add the mapping later.`,
          );
          // The vendor product exists, so a scoped reconcile is still valid.
          onCreated({ reconcile: reconcileAfter, sku: created.sku });
          return;
        }
      }
      onCreated({ reconcile: reconcileAfter, sku: created.sku });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create vendor product');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-label="Create vendor product"
      PaperProps={{
        sx: { background: 'transparent', boxShadow: 'none', borderRadius: 0, overflow: 'visible' },
      }}
    >
      <SidePanel
        title="Create vendor product"
        submitLabel={saving ? 'Creating…' : 'Create'}
        isValid={
          !saving && !!sku.trim() && !!name.trim() && (!linkProduct || !!selectedProduct)
        }
        onClose={() => {
          if (!saving) onClose();
        }}
        onSubmit={submit}
      >
        {error && <div className="side-panel__alert side-panel__alert--error">{error}</div>}

        <p className="recon-dialog__hint">
          New supplier SKU for {vendorName ?? 'the vendor'}. The next reconcile will
          match import rows carrying this SKU.
        </p>

        <FormField label="SKU *">
          <input value={sku} onChange={(e) => setSku(e.target.value)} />
        </FormField>
        <span className="recon-dialog__help">
          Must match the vendor's Offer Name / SKU exactly.
        </span>

        <FormField label="Display name *">
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </FormField>

        <SmartSelect
          label="Billing term"
          value={billingTerm}
          options={BILLING_TERM_OPTIONS}
          onChange={(v) => {
            if (v) setBillingTerm(v as BillingTerm);
          }}
        />

        <hr className="recon-dialog__divider" />

        <label className="side-panel__checkbox">
          <input
            type="checkbox"
            checked={linkProduct}
            onChange={(e) => setLinkProduct(e.target.checked)}
          />
          Also link to a commercial product (for billing coverage)
        </label>

        {linkProduct && (
          <>
            <Autocomplete
              className="recon-dialog__autocomplete"
              options={productOptions}
              loading={productsLoading}
              value={selectedProduct}
              onChange={(_, v) => setSelectedProduct(v)}
              getOptionLabel={(o) => o.displayName || o.sku || ''}
              getOptionKey={(o) => o.id}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              filterOptions={filterProducts}
              renderOption={(props, o) => {
                // key by id — several catalog rows share a display name (e.g.
                // duplicate "M365 Entra ID P1"); the SKU line tells them apart.
                const { key, ...liProps } = props as { key?: string };
                return (
                  <li {...liProps} key={o.id}>
                    <Stack>
                      <span>{o.displayName || o.sku}</span>
                      {o.sku && o.sku !== o.displayName && (
                        <Typography variant="caption" color="text.secondary">
                          {o.sku}
                        </Typography>
                      )}
                    </Stack>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} label="Commercial product" size="small" required />
              )}
            />
            <label className="side-panel__checkbox">
              <input
                type="checkbox"
                checked={showAllCategories}
                onChange={(e) => {
                  setShowAllCategories(e.target.checked);
                  // Clear a selection that the new filter would hide.
                  setSelectedProduct(null);
                }}
              />
              Show all product categories
            </label>
            <span className="recon-dialog__help">
              {showAllCategories
                ? 'Showing all active products.'
                : 'Showing active licenses/subscriptions only.'}
            </span>

            <FormField label="Seat multiplier">
              <input
                type="number"
                min={1}
                value={seatMultiplier}
                onChange={(e) => setSeatMultiplier(Number(e.target.value))}
              />
            </FormField>
            <span className="recon-dialog__help">
              Vendor seats expected per commercial seat (default 1).
            </span>
          </>
        )}

        <hr className="recon-dialog__divider" />

        <label className="side-panel__checkbox">
          <input
            type="checkbox"
            checked={reconcileAfter}
            onChange={(e) => setReconcileAfter(e.target.checked)}
          />
          Reconcile records with this new mapping
        </label>
        <span className="recon-dialog__help">
          Re-evaluates only the import rows carrying this SKU — not the whole
          batch — so the matched rows update immediately.
        </span>
      </SidePanel>
    </Dialog>
  );
};
